'use client';
import React, { useEffect, useRef } from 'react';

const values = [
  {
    icon: '🎯',
    title: 'تركيز كامل على مشروعك',
    description: 'نعمل مع عدد محدد جداً من الشركات لضمان إعطاء مشروعك الأولوية القصوى والاهتمام بالتفاصيل.',
  },
  {
    icon: '✨',
    title: 'تصاميم مخصصة 100%',
    description: 'لا نستخدم قوالب جاهزة أو حلولاً مكررة؛ كل تفصيلة تُبنى من الصفر لتعكس فخامة عملك.',
  },
  {
    icon: '💰',
    title: 'تركيز مطلق على الأرباح',
    description: 'هدفنا ليس مجرد شكل جميل، بل بناء نظام يعزز ثقة الزبائن ويضاعف المبيعات.',
  },
];

const pillars = [
  {
    icon: '🚀',
    title: 'استراتيجيات مخصصة لمضاعفة المبيعات',
    description: 'نصمم كل خطوة في مشروعك بهدف واحد: تحويل الزوار إلى زبائن يدفعون فوراً.',
  },
  {
    icon: '👑',
    title: 'بناء ثقة تجذب الزبائن الفاخرين',
    description: 'واجهات احترافية ومظهر قيادي يجعل عملاءك يفضلون التعامل معك دون التردد أو الفصال.',
  },
  {
    icon: '🎯',
    title: 'استهداف ذكي وأعلى عائد استثمار (ROI)',
    description: 'نضمن لك الوصول للجمهور الصح، لتقليل تكلفة الإعلانات وزيادة صافي أرباحك.',
  },
  {
    icon: '🔄',
    title: 'نظام أتمتة لضمان تكرار الشراء',
    description: 'أنظمة ذكية تبقي زبائنك متصلين بعلامتك التجارية لضمان عودتهم ولائهم لمشروعك باستمرار.',
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0', 'translate-x-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8', '-translate-x-8', 'translate-x-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #080808 0%, #0a0800 50%, #080808 100%)' }}
    >
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      <div className="orb orb-gold w-80 h-80 bottom-0 right-0 opacity-10"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Visual */}
          <div className="animate-on-scroll opacity-0 translate-x-8 transition-all duration-700 order-2 lg:order-1">
            <div className="relative">
              {/* Main card */}
              <div className="relative rounded-3xl overflow-hidden neon-border p-1">
                <div
                  className="rounded-3xl p-8"
                  style={{ background: 'linear-gradient(135deg, #0f0a00 0%, #0f0f0f 100%)' }}
                >
                  {/* Agency visual */}
                  <div className="text-center mb-8">
                    <div className="w-24 h-24 mx-auto rounded-3xl overflow-hidden gold-glow mb-4">
                      <img src="/assets/images/monetizX_agency_pfp_model_1-1786292005097.jpg" alt="MonetizX Logo" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="font-black text-2xl">
                      <span className="text-white">Monetiz</span><span style={{ color: '#f59e0b' }}>X</span>
                    </h3>
                    <p className="text-amber-600 text-sm">.agency</p>
                  </div>

                  {/* Skills */}
                  <div className="space-y-3">
                    {pillars?.map((pillar, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-amber-500/20 bg-amber-900/10 hover:border-amber-500/40 transition-colors">
                        <span className="text-xl flex-shrink-0 mt-0.5">{pillar?.icon}</span>
                        <div>
                          <h4 className="text-amber-400 font-bold text-sm leading-snug mb-1">{pillar?.title}</h4>
                          <p className="text-gray-400 text-xs leading-relaxed">{pillar?.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-amber-500 text-black font-black text-sm px-4 py-2 rounded-xl shadow-lg animate-float">
                استشارات ومشاريع مخصصة 1:1
              </div>
              <div className="absolute -bottom-4 -left-4 bg-gray-900 border border-amber-500/40 text-amber-400 font-bold text-sm px-4 py-2 rounded-xl shadow-lg animate-float-delay">
                2+ سنوات خبرة واحتراف
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="animate-on-scroll opacity-0 -translate-x-8 transition-all duration-700 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full section-badge text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              من نحن
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              نصنع براندات فاخرة
              <br />
              وأنظمة مبيعات
              <br />
              <span className="gradient-text">تضمن سيادتك في السوق</span>
            </h2>

            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              في <strong className="text-white">Monetiz</strong><strong style={{ color: '#f59e0b' }}>X</strong>، لا نقدم مجرد تصميم أو خدمات تقليدية، بل نبني لك هوية بصرية مخصصة وموقعاً إلكترونياً سريعاً يُحكِم قبضة مشروعك على السوق ويجذب أرباحاً حقيقية.
            </p>

            <p className="text-gray-400 leading-relaxed mb-8">
              فريقنا من الخبراء المتخصصين يجمع بين الإبداع والتقنية، نعمل بشغف على تحويل أفكارك إلى حلول رقمية تحقق نتائج حقيقية.
            </p>

            {/* Values */}
            <div className="space-y-4">
              {values?.map((value, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-amber-900/5 border border-amber-900/20 hover:border-amber-600/30 transition-colors">
                  <span className="text-2xl flex-shrink-0">{value?.icon}</span>
                  <div>
                    <h4 className="text-white font-bold mb-1">{value?.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{value?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
